import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Container, Button, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 1.5 }}>
      {number}. {title}
    </Typography>
    {children}
  </Box>
);

const Para = ({ children }) => (
  <Typography sx={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.85, mb: 1.5 }}>
    {children}
  </Typography>
);

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc', py: 5, fontFamily: "'Inter', sans-serif" }}>
      <Container maxWidth="md">
        {/* Back button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/login')}
          sx={{ mb: 3, textTransform: 'none', color: '#6366f1', fontWeight: 600, '&:hover': { background: 'rgba(99,102,241,0.06)' } }}
        >
          Back to Login
        </Button>

        {/* Card */}
        <Box
          sx={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
            p: { xs: 3, md: 5 },
            border: '1px solid #e8ecff',
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: '#0f172a', mb: 0.5 }}>
              Terms and Conditions
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              PayNTric Pvt Ltd · Last Updated: 2026
            </Typography>
          </Box>

          <Divider sx={{ mb: 4, borderColor: '#e8ecff' }} />

          {/* INTRODUCTION */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 1.5 }}>
              INTRODUCTION
            </Typography>
            <Para>
              I. These General Terms of Use are made between the legal entity incorporated under the Companies Act in the name <strong>PayNTric Pvt Ltd.</strong> (hereinafter: the PROVIDER or PayNTric) and you (hereinafter: the CLIENT and/or MERCHANT).
            </Para>
            <Para>
              II. By downloading, installing, or using all or any portion of the services or software of PayNTric (hereinafter: PayNTric Service or Service or Services), the CLIENT and/or MERCHANT agrees to be legally bound by these GENERAL TERMS and fully accepts all of the terms in the GENERAL TERMS. If the CLIENT and/or MERCHANT does not accept these GENERAL TERMS, then the CLIENT shall not be entitled to use or distribute the SERVICE.
            </Para>
            <Para>
              III. The CLIENT and/or MERCHANT agrees to these GENERAL TERMS on behalf of the company or other legal entity for which it is acting (for example, as an employee or contractor) or, if there is no company or legal entity, on behalf of yourself as an individual. By accepting these GENERAL TERMS the CLIENT and/or MERCHANT represents and warrants that:
            </Para>
            <Box sx={{ pl: 2.5, mb: 1.5 }}>
              {[
                'It has the requisite corporate power and official authority to execute, deliver and perform its obligations under these GENERAL TERMS;',
                'It has obtained all licenses, authorizations, approvals, consents, or permits required to perform its obligations under these GENERAL TERMS under all applicable laws of all authorities having jurisdiction over the SERVICES;',
                'The execution and performance of these GENERAL TERMS and the consummation of the transactions contemplated by these GENERAL TERMS have been duly authorized by the requisite corporate action on the part of such CLIENT and/or MERCHANT if required;',
                'The execution, delivery, and performance of these GENERAL TERMS does not violate any judgment, order, or decree; a material default under any material contract by which it or any of its material assets are bound; or an event that would, with notice or lapse of time, or both, constitute such a default.',
              ].map((item, i) => (
                <Typography key={i} sx={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.85, mb: 1, display: 'flex', gap: 1 }}>
                  <span style={{ fontWeight: 600, color: '#6366f1', minWidth: 20 }}>{String.fromCharCode(97 + i)})</span>
                  <span>{item}</span>
                </Typography>
              ))}
            </Box>
            <Para>
              IV. Note – other agreements concluded by the CLIENT and/or MERCHANT with PayNTric, if any, may refer to a separate name of this document. This shall not in any way affect the enforceability of these GENERAL TERMS.
            </Para>
          </Box>

          {/* Section 1 */}
          <Section number="1" title="DEFINITION OF TERMS">
            <Para>The following terms, used either in singular or plural, shall have the meaning as set below:</Para>
            {[
              ['1.1', 'GENERAL TERMS or TERMS', 'these general terms of use, including any annexes and attachments, and any additional agreements if concluded. These GENERAL TERMS also include terms of license attributable to the use or distribution of PayNTric Service;'],
              ['1.2', 'CLIENT', "the MERCHANT's customer/end-user who transacts through the MERCHANT's website or over the software, tools, or channels provided or accepted by PayNTric;"],
              ['1.3', 'PayNTric or SERVICE PROVIDER', 'the entity incorporated under the Companies Act under the name PayNTric Solutions Pvt Ltd. operating the PayNTric Service;'],
              ['1.4', 'PayNTric Service or SERVICE', 'the service and software offered by PayNTric that allows CLIENT and/or MERCHANT to pay for purchases or receive money through any payment channel;'],
              ['1.5', 'MERCHANT', 'an individual or entity entitled to distribute PayNTric Service to the CLIENT;'],
              ['1.6', 'CLIENT', 'an individual or entity who acquired access to the SERVICE;'],
              ['1.7', 'PAYMENT TRANSACTIONS or TRANSACTIONS', 'mean a legitimate transaction of payment, pay-out (to receive money), or settlement made with the CLIENT, performed in whole or in part via electronic communication and processed with the use of the SERVICE.'],
            ].map(([num, term, def]) => (
              <Typography key={num} sx={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.85, mb: 1.2 }}>
                <strong style={{ color: '#334155' }}>{num} {term}</strong> – {def}
              </Typography>
            ))}
          </Section>

          {/* Section 2 */}
          <Section number="2" title="GENERAL PROVISIONS, EFFECTIVE DATE">
            <Para>
              2.1. PayNTric is a corporation entitled to engage in the business concerning the provision of SERVICES regarding TRANSACTIONS, also by acquiring MERCHANTS to accept payments through various channels and systems.
            </Para>
            <Para>
              2.2. The MERCHANTS and BANKS have agreed with PayNTric to honor payments and TRANSACTIONS made through agreed means of TRANSACTIONS, under the terms and conditions hereinafter stipulated.
            </Para>
            <Para>
              2.3. The CLIENT and/or MERCHANT acknowledges that the copyright, designs, trademarks, and other intellectual property rights comprised in the information, text, graphics, scripts, software, technology, music, sound, photograph, or any other materials or works used or contained in the SERVICE or belonging to PayNTric are the sole and exclusive property of PayNTric and/or its affiliates, licensors or other third parties that hold rights to the particular part of SERVICE.
            </Para>
          </Section>

          <Divider sx={{ my: 4, borderColor: '#e8ecff' }} />

          {/* Nodal Officer Box */}
          {/* <Box
            sx={{
              background: '#f0f4ff',
              border: '1px solid #c7d2fe',
              borderRadius: '12px',
              p: 3,
              mb: 3,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#4338ca', mb: 1 }}>
              Nodal Officer / Customer Grievance
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.8 }}>
              Please write to: <strong>Mr. Yogesh</strong><br />
              Email: <a href="mailto:yogesh@jcentrix.com" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>yogesh@jcentrix.com</a><br />
              Mobile: <a href="tel:+919407157175" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>+91 9407157175</a>
            </Typography>
          </Box> */}

          {/* Company Details Box */}
          <Box
            sx={{
              background: '#fafbff',
              border: '1px solid #e8ecff',
              borderRadius: '12px',
              p: 3,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', mb: 1.5 }}>
              Company Details
            </Typography>
            {[
              ['Managed By', 'PayNTric Private Limited'],
              ['Address', '07, Golden Nest Appartment, Garhi, Chaukhandi, Noida, Gautam Buddha Nagar, Uttar Pradesh, 201307'],
              ['Email', 'support@jcentrix.com'],
              // ['Helpline', '+91 9407157175'],
            ].map(([label, value]) => (
              <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.8 }}>
                <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#64748b', minWidth: 90 }}>{label}:</Typography>
                <Typography sx={{ fontSize: '0.83rem', color: '#475569' }}>
                  {label === 'Email' ? (
                    <a href={`mailto:${value}`} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>{value}</a>
                  ) : label === 'Helpline' ? (
                    <a href={`tel:${value.replace(/\s/g, '')}`} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>{value}</a>
                  ) : value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsAndConditions;
